<script setup lang="ts">
import { gsap } from 'gsap'

const props = defineProps<{
  class?: string
  noDots?: boolean
}>()

const localePath = useLocalePath()

// Cycles through the same 6 shadow offsets the grid always used, now driven by
// the shared business-type list instead of a hardcoded, easily-drifting copy.
const SHADOWS = [
  'md:shadow-[6px_6px_0_rgba(46,131,255,0.4)]',
  'md:shadow-[0px_6px_0_rgba(46,131,255,0.4)]',
  'md:shadow-[-6px_6px_0_rgba(46,131,255,0.4)]',
  'md:shadow-[6px_-6px_0_rgba(46,131,255,0.4)]',
  'md:shadow-[0px_-6px_0_rgba(46,131,255,0.4)]',
  'md:shadow-[-6px_-6px_0_rgba(46,131,255,0.4)]',
]

const businessTypes = useBusinessTypes().map((type, i) => ({
  key: type.i18nKey,
  icon: type.icon,
  shadow: SHADOWS[i % SHADOWS.length],
  url: `/business-types/${type.slug}`,
}))

const section = ref<HTMLElement | null>(null)

onMounted(() => {
  const el = section.value
  if (!el) return

  // Heading block
  gsap.fromTo(el.querySelector('.op-heading'),
    { opacity: 0, y: 50 },
    {
      opacity: 1, y: 0, duration: 0.9, ease: 'power3.out',
      scrollTrigger: { trigger: el, start: 'top 80%', once: true },
    },
  )

  // Service cards: simple fade + rise, staggered
  gsap.fromTo(el.querySelectorAll('.op-card'),
    { opacity: 0, y: 30 },
    {
      opacity: 1, y: 0, duration: 0.6, ease: 'power2.out', stagger: 0.08,
      scrollTrigger: { trigger: el.querySelector('.op-grid'), start: 'top 85%', once: true },
    },
  )

  // CTA
  gsap.fromTo(el.querySelector('.op-cta'),
    { opacity: 0, y: 30, scale: 0.9 },
    {
      opacity: 1, y: 0, scale: 1, duration: 0.7, ease: 'back.out(1.4)',
      scrollTrigger: { trigger: el.querySelector('.op-cta'), start: 'top 92%', once: true },
    },
  )
})
</script>

<template>
  <!--
    `:class="class"` was a parse error — `class` is a reserved word, so the
    expression never compiled and the caller's class was silently dropped.
    Reading it off `props` is the same value through a valid expression.
  -->
  <section id="platform" ref="section" class="py-12 md:py-16 lg:py-20 bg-white text-center relative z-0" :class="props.class">
    <!-- Dot grid background -->
    <div v-if="!noDots" class="absolute inset-0 bg-[radial-gradient(circle,_#d1d5db_1.5px,_transparent_1.5px)] [background-size:28px_28px] opacity-50 pointer-events-none" aria-hidden="true"/>

    <div class="container relative">
      <div class="op-heading">
        <div class="bfm-badge px-3 py-1.5 md:px-4 md:py-2 text-sm md:text-base mb-5 bg-heart-purple text-white">
          <UIcon name="i-approved-arrow-upscale" class="text-white size-4 md:size-5" />
          {{ $t('page.home.one_platform.badge') }}
        </div>

        <h2 class="text-[clamp(1.75rem,3.5vw,3rem)] font-bold leading-[1.25] text-deep-indigo mb-3">{{ $t('page.home.one_platform.title') }}</h2>
        <p class="text-base md:text-lg lg:text-xl text-overcast-night max-w-xl mx-auto mb-10 md:mb-12">
          {{ $t('page.home.one_platform.subtitle') }}
        </p>
      </div>

      <div class="op-grid grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 mb-10 md:mb-12 [perspective:1000px]">
        <a v-for="type in businessTypes" :key="type.key" v-tilt :href="localePath(type.url)" :class="['op-card relative flex flex-col items-center justify-center gap-1.5 md:gap-2.5 py-6 md:py-8 lg:py-10 bg-white border-2 border-sky-blue rounded-lg cursor-pointer shadow-[4px_4px_0_rgba(46,131,255,0.4)]', type.shadow]">
          <img :src="type.icon" :alt="$t('page.home.one_platform.business_types.' + type.key)" class="size-12 sm:size-14 md:size-16 lg:size-20" >
          <span class="text-sm sm:text-base md:text-lg lg:text-xl font-normal text-deep-indigo text-center px-2">{{ $t('page.home.one_platform.business_types.' + type.key) }}</span>
        </a>
      </div>

      <div class="op-cta mt-2">
        <a v-magnetic :href="localePath('/request-demo')" class="bfm-btn bfm-btn-md md:bfm-btn-lg bg-cheese-yellow">
          {{ $t('page.home.one_platform.cta') }}
        </a>
      </div>
    </div>
  </section>
</template>
